import React, { useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { useTranslation } from "react-i18next";
import { getStateChipColor, getStateLabel, resolveStateId } from "./statusHelpers";

export default function StatusChangeDialog({
    open,
    onClose,
    row,
    entityLabel,
    requestStates,
    onConfirm,
}) {
    const { t } = useTranslation();
    const [newStateId, setNewStateId] = useState("");
    const [comment, setComment] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const currentStateId = resolveStateId(row);

    useEffect(() => {
        if (open) {
            setNewStateId("");
            setComment("");
            setError("");
        }
    }, [open, row?.id]);

    const handleConfirm = async () => {
        if (!newStateId) {
            setError("Выберите новый статус");
            return;
        }
        if (Number(newStateId) === Number(currentStateId)) {
            setError("Новый статус совпадает с текущим");
            return;
        }

        setLoading(true);
        setError("");
        try {
            await onConfirm(row, currentStateId, Number(newStateId), comment);
            onClose();
        } catch (e) {
            setError(e?.message || String(e));
        } finally {
            setLoading(false);
        }
    };

    if (!row) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <SwapHorizIcon color="primary" />
                Изменение статуса
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        {entityLabel}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        № {row.number || "—"} &nbsp;·&nbsp; ID {row.id}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 3,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: "grey.50",
                    }}
                >
                    <Box>
                        <Typography variant="caption" color="text.secondary">
                            Текущий статус
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                            <Chip
                                label={getStateLabel(row.state, t)}
                                color={getStateChipColor(row.state?.code)}
                                size="small"
                            />
                        </Box>
                    </Box>
                    <SwapHorizIcon color="action" />
                    <Box sx={{ flex: 1 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Новый статус</InputLabel>
                            <Select
                                value={newStateId}
                                label="Новый статус"
                                onChange={(e) => setNewStateId(e.target.value)}
                            >
                                {requestStates.map((s) => (
                                    <MenuItem
                                        key={s.id}
                                        value={s.id}
                                        disabled={Number(s.id) === Number(currentStateId)}
                                    >
                                        {s.name || s.code}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Комментарий (необязательно)"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Причина изменения статуса..."
                />

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}

                <Alert severity="warning" sx={{ mt: 2 }}>
                    Изменение статуса выполняется через workflow API. Убедитесь, что переход
                    допустим для данного типа документа.
                </Alert>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={loading}>
                    Отмена
                </Button>
                <LoadingButton
                    variant="contained"
                    loading={loading}
                    onClick={handleConfirm}
                    disabled={!newStateId}
                >
                    Применить
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}
